/**
 * PRTimeline — Shows personal record history with improvements.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, FlatList } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { supabase } from '@/lib/supabase';
import { TrendingUp, Award, Lock } from 'lucide-react-native';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { useUnits } from '@/lib/useUnits';

interface PREntry {
  id: string;
  lift_key: string;
  weight_kg: number;
  previous_pr_kg: number | null;
  improvement_kg: number | null;
  improvement_pct: number | null;
  context_type: string | null;
  video_url: string | null;
  performed_at: string;
}

interface PRTimelineProps {
  profileId: string;
  limit?: number;
}

const LIFT_EMOJIS: Record<string, string> = {
  'bench-press': '🏋️',
  'back-squat': '🦵',
  'deadlift': '💀',
  'overhead-press': '🔝',
  'weighted-pull-up': '💪',
};

export default function PRTimeline({ profileId, limit = 10 }: PRTimelineProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { tier } = useSubscription();
  const isPro = hasAtLeast(tier, 'pro');
  const { displayWeight, unitLabel } = useUnits();
  const [prs, setPrs] = useState<PREntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) return;
    const load = async () => {
      // First try the pr_timeline table
      const { data: tlData } = await supabase
        .from('pr_timeline')
        .select('*')
        .eq('profile_id', profileId)
        .order('performed_at', { ascending: false })
        .limit(limit);

      if (tlData && tlData.length > 0) {
        setPrs(tlData);
        setLoading(false);
        return;
      }

      // Fallback: derive PRs from strength_events (best weight per lift_key)
      // The first-ever logged weight for any exercise IS a PR by definition.
      const { data: evts } = await supabase
        .from('strength_events')
        .select('id, lift_key, weight_kg, performed_at, video_url')
        .eq('profile_id', profileId)
        .order('performed_at', { ascending: false })
        .limit(200);

      if (!evts || evts.length === 0) {
        setPrs([]);
        setLoading(false);
        return;
      }

      // Find best weight per lift_key and build synthetic PREntry list
      const bestByLift: Record<string, typeof evts[0]> = {};
      // Process chronologically to detect improvements
      const chrono = [...evts].sort((a, b) => new Date(a.performed_at).getTime() - new Date(b.performed_at).getTime());
      const runningBest: Record<string, number> = {};
      const syntheticPRs: PREntry[] = [];

      for (const evt of chrono) {
        const prev = runningBest[evt.lift_key] ?? null;
        const isFirstOrBetter = prev === null || evt.weight_kg > prev;
        if (isFirstOrBetter) {
          const improvementKg = prev !== null ? evt.weight_kg - prev : null;
          syntheticPRs.push({
            id: evt.id,
            lift_key: evt.lift_key,
            weight_kg: evt.weight_kg,
            previous_pr_kg: prev,
            improvement_kg: improvementKg,
            improvement_pct: prev !== null && prev > 0 ? ((evt.weight_kg - prev) / prev) * 100 : null,
            context_type: 'training',
            video_url: evt.video_url ?? null,
            performed_at: evt.performed_at,
          });
          runningBest[evt.lift_key] = evt.weight_kg;
        }
      }

      // Return most recent first, limited
      setPrs(syntheticPRs.reverse().slice(0, limit));
      setLoading(false);
    };
    load();
  }, [profileId, limit]);

  if (loading) return null;

  if (prs.length === 0) {
    return (
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={styles.header}>
          <Award size={14} color={c.accent} />
          <Text style={[styles.title, { color: c.accent, fontFamily: TYPO.heading.bold }]}>PR TIMELINE</Text>
        </View>
        <Text style={[styles.emptyText, { color: c.textTertiary }]}>
          No PRs yet — log a workout to start tracking
        </Text>
      </View>
    );
  }

  const displayPrs = isPro ? prs : prs.slice(0, 3);

  return (
    <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.header}>
        <Award size={14} color={c.accent} />
        <Text style={[styles.title, { color: c.accent, fontFamily: TYPO.heading.bold }]}>PR TIMELINE</Text>
      </View>

      {displayPrs.map((pr) => {
        const liftName = pr.lift_key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const emoji = LIFT_EMOJIS[pr.lift_key] || '🏋️';
        const date = new Date(pr.performed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return (
          <View key={pr.id} style={styles.entry}>
            <View style={styles.timeline}>
              <View style={[styles.dot, { backgroundColor: c.accent }]} />
              <View style={[styles.line, { backgroundColor: c.border }]} />
            </View>
            <View style={styles.entryContent}>
              <View style={styles.entryHeader}>
                <Text style={styles.emoji}>{emoji}</Text>
                <Text style={[styles.liftName, { color: c.text, fontFamily: TYPO.body.semibold }]}>{liftName}</Text>
                <Text style={[styles.date, { color: c.textTertiary }]}>{date}</Text>
              </View>
              <View style={styles.entryStats}>
                <Text style={[styles.weight, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                  {Math.round(displayWeight(pr.weight_kg, 'kg'))}{unitLabel}
                </Text>
                {pr.improvement_kg && pr.improvement_kg > 0 && (
                  <View style={[styles.improveBadge, { backgroundColor: (c.success || '#22c55e') + '20' }]}>
                    <TrendingUp size={10} color={c.success || '#22c55e'} />
                    <Text style={[styles.improveText, { color: c.success || '#22c55e' }]}>
                      +{Math.round(displayWeight(pr.improvement_kg, 'kg'))}{unitLabel}
                      {pr.improvement_pct ? ` (${Math.round(pr.improvement_pct)}%)` : ''}
                    </Text>
                  </View>
                )}
                {pr.video_url && <Text style={styles.videoIcon}>📹</Text>}
              </View>
            </View>
          </View>
        );
      })}

      {!isPro && prs.length > 3 && (
        <View style={styles.proCta}>
          <Lock size={12} color={c.textTertiary} />
          <Text style={[styles.proCtaText, { color: c.textTertiary }]}>
            See full PR history — Pro
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.sm, marginHorizontal: SPACE.md, marginBottom: SPACE.md },
  emptyText: { fontSize: 13, fontFamily: TYPO.body.regular, paddingVertical: SPACE.sm },
  header: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  title: { fontSize: 11, letterSpacing: 1.5 },
  entry: { flexDirection: 'row', gap: 10 },
  timeline: { alignItems: 'center', width: 12 },
  dot: { width: 8, height: 8, borderRadius: 4, marginTop: 6 },
  line: { width: 1, flex: 1, marginTop: 4 },
  entryContent: { flex: 1, gap: 2, paddingBottom: SPACE.sm },
  entryHeader: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  emoji: { fontSize: 14 },
  liftName: { fontSize: 13, flex: 1 },
  date: { fontSize: 11 },
  entryStats: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 2 },
  weight: { fontSize: 18 },
  improveBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: RADIUS.xs },
  improveText: { fontSize: 11, fontWeight: '600' },
  videoIcon: { fontSize: 12 },
  proCta: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingTop: 8, paddingLeft: 22 },
  proCtaText: { fontSize: 12 },
});
